import FormConfig from '../ConfigurationFormConfig';
import MathUtility from '../../Utility/MathUtility';

/**
 * KiWizardManager (DWA-A 138-1)
 *
 * Responsibility:
 * - Step 1: decision if k_i is available
 * - If yes: collect mantissa/exponent and persist `ki` + `kiIsManual`
 * - If yes: hide Step 2–5 (skip) and show Step 6
 */
export default class KiWizardManager {
    /**
     * @param {jQuery} form
     * @param {Object} state
     * @param {Object} host instance of ConfigurationForm
     */
    constructor(form, state, host) {
        this.form = form;
        this.state = state;
        this.host = host;

        this.$step = $('#kiStep1');
        this.$wrapper = this.$step.closest('.kiWizardStep1');
        this.$indexNotationResult = $(FormConfig.selectors.kiIndexNotationResult);

        // Downstream slide content wrappers that are skipped when ki is manual
        this.$kStep2 = $('.kWizardStep2');
        this.$fKStep3 = $('.fKWizardStep3');
        this.$fOrtStep4 = $('.fortWizardStep4');
        this.$fOrtStep5 = $('.fortWizardStep5');
        this.$fMethodeStep6 = $('.fMethodeWizardStep6');

        this.wasActive = false;
		this.kiValue = 0;
    }

    init() {
        if (!this.$step.length) {
            return;
        }
        this.bindHandlers();
        this.updateVisibility();
        this.syncFromPersisted();
		this.recomputeKi();
    }

    bindHandlers() {
        this.form.on('change', '#kiStep1 .kiDecision', e => {
            // radios without name: enforce exclusivity
            this.form.find('#kiStep1 .kiDecision').not(e.target).prop('checked', false);

            const decision = this.getDecision();
            if (decision === 1) {
                this.setKiIsManual(true);
                this.$step.find('.kiValueRow').show();
                this.$step.find('.kiSignificand, .kiExponent').prop('required', true);
                this.recomputeKi();
            } else if (decision === 0) {
                this.setKiIsManual(false);
                this.setKi(0);
                this.$step.find('.kiValueRow').hide();
                this.$step.find('.kiSignificand, .kiExponent').prop('required', false).val('');
            }

            this.updateVisibility();
			this.triggerDownstreamUpdate();
        });

		this.form.on('input', '#kiStep1 .kiSignificand', () => {
			if (this.getDecision() === 1) {
				this.recomputeKi();
			}
		});
		this.form.on('change', '#kiStep1 .kiExponent', () => {
			if (this.getDecision() === 1) {
				this.recomputeKi();
			}
		});

		const slide = $(FormConfig.selectors.kiSlide);
		slide.find('input, select').on('change', () => {
			this.host.clearSlides(slide.nextAll(FormConfig.selectors.slides))
		});
	}

	triggerDownstreamUpdate() {
		// Refresh downstream visibility/skip logic
		if (this.host && this.host.fkWizardManager && typeof this.host.fkWizardManager.updateVisibility === 'function') {
			this.host.fkWizardManager.updateVisibility();
		}
		if (this.host && this.host.fOrtQuestionManager && typeof this.host.fOrtQuestionManager.updateVisibility === 'function') {
			this.host.fOrtQuestionManager.updateVisibility();
		}
		if (this.host && this.host.fMethodeManager && typeof this.host.fMethodeManager.updateVisibility === 'function') {
			this.host.fMethodeManager.updateVisibility();
		}
	}

    updateVisibility() {
        const isActive = (typeof this.host.isGuideline1381Active === 'function')
            ? this.host.isGuideline1381Active()
            : false;

        if (!isActive && this.wasActive) {
            this.reset();
        }

        if (isActive) {
            this.$wrapper.show();
            this.$step.show();
            if (typeof this.host.setInputsDisabled === 'function') {
                this.host.setInputsDisabled(this.$wrapper, false);
            }
        } else {
            if (typeof this.host.setInputsDisabled === 'function') {
                this.host.setInputsDisabled(this.$wrapper, true);
            }
            this.$step.hide();
            this.$wrapper.hide();
        }

		// Skip logic: if ki is manual, hide step2–5 and ensure step6 is available
		if (isActive) {
			const kiIsManual = this.getPersistedBool('.kiIsManual');
			console.log('[KiWizardManager] updateVisibility skip check', { kiIsManual });
			if (kiIsManual) {
				this.$kStep2.hide();
				this.host.setInputsDisabled(this.$kStep2, true);
				this.$fKStep3.hide();
				this.host.setInputsDisabled(this.$fKStep3, true);
				this.$fOrtStep4.hide();
				this.host.setInputsDisabled(this.$fOrtStep4, true);
				this.$fOrtStep5.hide();
				this.host.setInputsDisabled(this.$fOrtStep5, true);
				this.$fMethodeStep6.show();
				this.host.setInputsDisabled(this.$fMethodeStep6, false);
			} else {
				// Restore hidden steps when switching back to "No"
				this.$kStep2.show();
				this.host.setInputsDisabled(this.$kStep2, false);
				this.$fKStep3.show();
				this.host.setInputsDisabled(this.$fKStep3, false);
				this.$fOrtStep4.show();
				this.host.setInputsDisabled(this.$fOrtStep4, false);
				this.$fOrtStep5.show();
				this.host.setInputsDisabled(this.$fOrtStep5, false);
			}
		}

        this.wasActive = isActive;
    }

    syncFromPersisted() {
        const kiIsManual = this.getPersistedBool('.kiIsManual');
        console.log('[KiWizardManager] syncFromPersisted', { kiIsManual });
        if (kiIsManual === true) {
            this.form.find('#kiStep1 .kiDecision[value="1"]').prop('checked', true);
            this.$step.find('.kiValueRow').show();
            this.$step.find('.kiSignificand, .kiExponent').prop('required', true);

            // Recover significand and exponent from the full kiValue
            const $field = this.form.find('input.kiValue').first();
            const fullValue = parseFloat($field.val() || '0');
            if (fullValue > 0) {
                // Find best matching exponent from select options
                const $exponentSelect = this.$step.find('.kiExponent');
                const exponents = $exponentSelect.find('option').map((i, opt) => $(opt).val()).get().filter(v => v !== '');

                let bestExp = -4; // Default to -4 as it's common
                let bestMantissa = fullValue / Math.pow(10, bestExp);

                for (const expStr of exponents) {
                    const exp = parseInt(expStr, 10);
                    const mantissa = fullValue / Math.pow(10, exp);
                    // If mantissa is between 0.99 and 10.01, it's a good match
                    if (mantissa >= 0.99 && mantissa < 10.01) {
                        bestExp = exp;
                        bestMantissa = mantissa;
                        break;
                    }
                }

                this.$step.find('.kiSignificand').val(MathUtility.formatFloat(Math.round(bestMantissa * 100) / 100));
                $exponentSelect.val(String(bestExp));

                // Set result display without triggering change
                this.$indexNotationResult.html(`(${MathUtility.formatFloat(fullValue)})`);
            }
        } else if (kiIsManual === false) {
            this.form.find('#kiStep1 .kiDecision[value="0"]').prop('checked', true);
            this.$step.find('.kiValueRow').hide();
            this.$step.find('.kiSignificand, .kiExponent').prop('required', false);
        } else {
            // Default: if it's explicitly guideline 138-1 and no value set, pre-select "No"
            const isActive = (typeof this.host.isGuideline1381Active === 'function')
                ? this.host.isGuideline1381Active()
                : false;
            console.log('[KiWizardManager] syncFromPersisted fallback', { isActive });
            if (isActive) {
                this.form.find('#kiStep1 .kiDecision[value="0"]').prop('checked', true);
                this.setKiIsManual(false);
            }
        }
    }

    getDecision() {
        const $checked = this.form.find('#kiStep1 .kiDecision:checked');
        if ($checked.length) {
            return parseInt($checked.val(), 10);
        }
        return null;
    }

    recomputeKi() {
        const rawMantissa = String(this.$step.find('.kiSignificand').val() || '').trim().replace(',', '.');
        const rawExponent = String(this.$step.find('.kiExponent').val() || '').trim();
        if (rawMantissa === '' || rawExponent === '') {
            this.setKi(0);
            this.$indexNotationResult.html('');
            return;
        }

        const value = MathUtility.resolveIndexNotation(rawMantissa, rawExponent);
        this.setKi(value);

        // Show formatted result behind inputs
        this.$indexNotationResult.html(`(${MathUtility.formatFloat(value)})`);
    }

    setKi(value, triggerChange = true) {
        const $field = this.form.find('input.kiValue').first();
        if (!$field.length) {
            return;
        }
		this.kiValue = value;
        $field.val(String(value));
        if (triggerChange) {
            $field.trigger('change');
        }
    }

	getKi() {
		return this.kiValue;
	}

    setKiIsManual(enabled) {
        const $field = this.form.find('input.kiIsManual').first();
        if (!$field.length) {
            return;
        }
        $field.val(enabled ? '1' : '0');
    }

	isKiManual() {
		return this.getPersistedBool('.kiIsManual');
	}

	isKiInPermissableRange() {
		return this.getKi() >= 0.000001 && this.getKi() <= 0.001;
	}

    getPersistedBool(selector) {
        const $field = this.form.find(selector).first();
        if (!$field.length) {
            return null;
        }
        const raw = String($field.val() || '').trim();
        if (raw === '') {
            return null;
        }
        return raw === '1' || raw === 'true';
    }

    reset() {
        this.form.find('#kiStep1 .kiDecision').prop('checked', false);
        this.$step.find('.kiSignificand, .kiExponent').val('').prop('required', false);
        this.$step.find('.kiValueRow').hide();
        this.$indexNotationResult.html('');
        this.setKiIsManual(false);
        this.setKi(0);
    }
}
